import { Box, Typography } from '@mui/material';
import type { ProductConsumption } from '@vy/types';
import { isEmptyArray, isNotEmptyArray } from '@vy/utils';
import React, { useCallback, useMemo, useState } from 'react';
import { useGetCustomerPortalDailyUsageData } from '../../../../../hooks/requests/use-customer-portal';
import { useCustomerPortalDailyUsageChart } from '../../../hooks';
import { ChartDataKeyEnum } from '../../../hooks/use-customer-portal-chart-data-utils';
import {
  CUSTOMER_PORTAL_BACKGROUND_COLOR,
  CUSTOMER_PORTAL_HEADING_COLOR,
} from '../customer-portal-colors';
import { CustomerPortalChart } from '../CustomerPortalChart.tsx/CustomerPortalChart';
import { CustomerPortalMonthSelector } from './CustomerPortalMonthSelector';
import { ProductVisibilityCheckboxes } from './ProductVisibilityCheckboxes';
import { ProductCommitmentProgressBars } from './ProductCommitmentProgressBars';

const MINIMUM_PRODUCTS_FOR_CHECKBOXES = 2;

type CustomerPortalChartSectionProps = {
  token: string;
  invoiceId: string;
  productsConsumption?: ProductConsumption[];
};

export const CustomerPortalChartSection: React.FC<CustomerPortalChartSectionProps> = ({
  token,
  invoiceId,
  productsConsumption,
}) => {
  const { data: dailyUsageData } = useGetCustomerPortalDailyUsageData({ token, invoiceId }, { retry: false });

  const {
    chartData,
    chartSeries,
    availableMonths,
    selectedMonth,
    setSelectedMonth,
    shouldShowMonthSelector,
    xAxisValueFormatter,
    tooltipTitle,
  } = useCustomerPortalDailyUsageChart(dailyUsageData);

  const [hiddenProducts, setHiddenProducts] = useState<Set<string>>(new Set());

  const handleToggleProduct = useCallback((productName: string) => {
    setHiddenProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productName)) {
        next.delete(productName);
      } else {
        next.add(productName);
      }
      return next;
    });
  }, []);

  const checkboxProducts = useMemo(() => {
    return chartSeries.map((series) => ({ productName: series.label, color: series.color }));
  }, [chartSeries]);

  const visibleSeries = useMemo(() => {
    return chartSeries.filter((series) => !hiddenProducts.has(series.label));
  }, [chartSeries, hiddenProducts]);

  const visibleCommitments = useMemo<ProductConsumption[]>(() => {
    if (!isNotEmptyArray(productsConsumption)) {
      return [];
    }

    return productsConsumption.filter(
      (productConsumption) => productConsumption.provisionedAmount > 0 && productConsumption.consumedAmount > 0,
    );
  }, [productsConsumption]);

  if (isEmptyArray(chartData)) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: CUSTOMER_PORTAL_BACKGROUND_COLOR,
        padding: '24px 32px 16px 24px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            color: CUSTOMER_PORTAL_HEADING_COLOR,
            fontSize: '16px',
            lineHeight: '24px',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Invoice usage breakdown
        </Typography>

        {shouldShowMonthSelector && (
          <CustomerPortalMonthSelector
            availableMonths={availableMonths}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
          />
        )}
      </Box>

      {isNotEmptyArray(visibleCommitments) && (
        <ProductCommitmentProgressBars commitments={visibleCommitments} />
      )}

      {isNotEmptyArray(checkboxProducts) && checkboxProducts.length >= MINIMUM_PRODUCTS_FOR_CHECKBOXES && (
        <ProductVisibilityCheckboxes
          products={checkboxProducts}
          hiddenProducts={hiddenProducts}
          onToggle={handleToggleProduct}
        />
      )}

      <CustomerPortalChart
        chartData={chartData}
        chartSeries={visibleSeries}
        xAxisValueFormatter={xAxisValueFormatter}
        tooltipTitle={tooltipTitle}
        barWidthTrim={3}
        chartMargins={{ left: 70, right: 30, top: 20, bottom: 20 }}
        xAxisDataKey={ChartDataKeyEnum.DAY}
      />
    </Box>
  );
};
